import { useRef, useState } from "react";
import { Database, Download, FlaskConical, RotateCcw, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CHUNK = 4 * 1024 * 1024;
const CARDS = [
  { key: "backup", icon: Download, title: "Backup Database", text: "Unduh seluruh data beserta semua foto dalam satu file ZIP.", btn: "Unduh Backup", tone: "bg-primary/12 text-primary" },
  { key: "restore", icon: Upload, title: "Upload Restore Database", text: "Pulihkan data dari file backup ZIP. Semua data saat ini akan diganti.", btn: "Pilih File", tone: "bg-sky-500/15 text-sky-600", danger: true },
  { key: "demo", icon: FlaskConical, title: "Database Dummy (Demo)", text: "Hapus semua data lalu isi dengan data demo (wilayah, cabang, petugas, anggota, pinjaman).", btn: "Isi Data Demo", tone: "bg-amber-500/15 text-amber-600", danger: true },
  { key: "reset", icon: RotateCcw, title: "Reset Database", text: "Hapus semua data kecuali akun super admin dan pengaturan aplikasi.", btn: "Reset", tone: "bg-rose-500/15 text-rose-600", danger: true },
];

const saveBlob = (blob, name) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
};

const uploadChunks = async (file, onProgress) => {
  const id = `r-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const n = Math.max(1, Math.ceil(file.size / CHUNK));
  for (let i = 0; i < n; i += 1) {
    const fd = new FormData();
    fd.append("upload_id", id); fd.append("index", i); fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), "part");
    await api.post("/admin/db/restore/chunk", fd);
    onProgress(Math.round(((i + 1) / n) * 100));
  }
  return id;
};

export default function DatabasePage() {
  const [act, setAct] = useState(null);
  const [pw, setPw] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const open = (c) => { setPw(""); setFile(null); setAct(c); };
  const run = async () => {
    setBusy("Memproses...");
    try {
      if (act.key === "backup") {
        const r = await api.post("/admin/db/backup", { password: pw }, { responseType: "blob" });
        saveBlob(r.data, `backup-ksp-${new Date().toISOString().slice(0, 10)}.zip`);
        toast.success("Backup berhasil diunduh");
      } else if (act.key === "restore") {
        await api.post("/admin/db/verify", { password: pw });
        const id = await uploadChunks(file, (p) => setBusy(`Mengunggah ${p}%`));
        setBusy("Memulihkan data...");
        const { data } = await api.post("/admin/db/restore/finish", { upload_id: id, password: pw });
        toast.success(`Restore selesai: ${data.documents} data, ${data.files} foto`);
      } else if (act.key === "demo") {
        const { data } = await api.post("/admin/db/demo", { password: pw });
        toast.success(`Data demo dibuat. Password akun demo: ${data.password}`, { duration: 10000 });
      } else {
        const { data } = await api.post("/admin/db/reset", { password: pw });
        toast.success(`Reset selesai, ${data.deleted} data dihapus`);
      }
      setAct(null);
    } catch (e) {
      const msg = e.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : errMsg(e);
      toast.error(msg);
    } finally { setBusy(""); }
  };
  return (
    <div className="max-w-4xl">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-5 flex items-center gap-2"><Database className="h-7 w-7 text-primary" />Pengaturan Database</h1>
      <div className="grid gap-4 sm:grid-cols-2">
        {CARDS.map((c) => (
          <div key={c.key} className="panel !p-5 flex flex-col" data-testid={`db-card-${c.key}`}>
            <span className={`h-11 w-11 rounded-2xl grid place-items-center ${c.tone}`}><c.icon className="h-5 w-5" /></span>
            <p className="font-heading font-semibold mt-3">{c.title}</p>
            <p className="text-sm text-muted-foreground mt-1 flex-1">{c.text}</p>
            <Button className={`mt-4 h-10 rounded-full ${c.danger ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}`} onClick={() => open(c)} data-testid={`db-${c.key}-button`}>{c.btn}</Button>
          </div>
        ))}
      </div>
      <Dialog open={Boolean(act)} onOpenChange={(o) => !o && !busy && setAct(null)}>
        <DialogContent className="sm:max-w-md" data-testid="db-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{act?.title}</DialogTitle>
            <DialogDescription>{act?.danger ? "Tindakan ini tidak bisa dibatalkan. " : ""}Masukkan password super admin untuk melanjutkan.</DialogDescription>
          </DialogHeader>
          {act?.key === "restore" && (
            <div>
              <input ref={fileRef} type="file" accept=".zip,application/zip" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="db-restore-file" />
              <Button variant="outline" className="w-full h-11 rounded-full" onClick={() => fileRef.current?.click()}>{file ? `${file.name} (${(file.size / 1048576).toFixed(1)} MB)` : "Pilih file backup (.zip)"}</Button>
            </div>
          )}
          <Input type="password" className="h-11" placeholder="Password super admin" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="db-password-input" />
          <Button className={`h-11 rounded-full ${act?.danger ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}`} disabled={!pw || Boolean(busy) || (act?.key === "restore" && !file)} onClick={run} data-testid="db-confirm-submit">{busy || "Lanjutkan"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
